import {
  ScrollAreaCorner,
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "reka-ui";
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

const ScrollBar = defineComponent({
  name: "ScrollBar",
  props: {
    orientation: p.string<"vertical" | "horizontal">(),
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    forceMount: p.boolean(),
    scrollbarSize: p.string<"xs" | "sm" | "md" | "lg">("md"),
  },
  setup(props, { attrs }) {
    const scrollbarSize = computed(() => {
      switch (props.scrollbarSize) {
        case "xs":
          return "data-[orientation=horizontal]:h-1.5 data-[orientation=vertical]:w-1.5";
        case "sm":
          return "data-[orientation=horizontal]:h-2 data-[orientation=vertical]:w-2";
        case "md":
          return "data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5";
        case "lg":
          return "data-[orientation=horizontal]:h-3 data-[orientation=vertical]:w-3";
      }
    });

    return () => (
      <ScrollAreaScrollbar
        {...props}
        class={cn(
          "group z-60 flex touch-none select-none p-0.5 transition-colors duration-200 ease-out first-letter:flex data-[orientation=horizontal]:flex-col",
          scrollbarSize.value,
          attrs.class ?? ""
        )}
        forceMount={props.forceMount}
      >
        <ScrollAreaThumb
          class={cn(
            "z-60 flex-1 rounded-full bg-muted-foreground/50 group-hover:bg-muted-foreground"
          )}
        />
      </ScrollAreaScrollbar>
    );
  },
});

export default defineComponent({
  name: "ScrollArea",
  props: {
    orientation: p.string<"vertical" | "horizontal" | "both">("vertical"),
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    forceMount: p.boolean(),
    type: p.string<"auto" | "always" | "scroll" | "hover">("scroll"),
    dir: p.string<"ltr" | "rtl">(),
    scrollHideDelay: p.number(),
    class: p.string(),
    rootClass: p.string(),
    scrollbarSize: p.string<"xs" | "sm" | "md" | "lg">("md"),
    nearBottomThreshold: p.number(120),
    onScrollChange: p.func<(nearBottom: boolean) => void>(),
  },
  setup(props, { slots, expose }) {
    const viewportComp = ref<{ $el: HTMLElement } | null>(null);
    let scrollEl: HTMLElement | null = null;

    function getScrollEl() {
      return viewportComp.value?.$el.parentElement ?? null;
    }

    function isNearBottom() {
      const el = getScrollEl();
      if (!el) return true;
      return el.scrollHeight - el.scrollTop - el.clientHeight <= props.nearBottomThreshold;
    }

    function handleScroll() {
      props.onScrollChange?.(isNearBottom());
    }

    onMounted(() => {
      scrollEl = getScrollEl();
      scrollEl?.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();
    });

    onBeforeUnmount(() => {
      scrollEl?.removeEventListener("scroll", handleScroll);
      scrollEl = null;
    });

    expose({
      scrollToBottom(options?: { behavior?: "auto" | "smooth" }) {
        const scrollableEl = viewportComp.value?.$el.parentElement;
        if (!scrollableEl) return;
        scrollableEl.scrollTo({
          top: viewportComp.value?.$el.scrollHeight,
          behavior: options?.behavior ?? "smooth",
        });
      },
      scrollToTop(options?: { behavior?: "auto" | "smooth" }) {
        const scrollableEl = viewportComp.value?.$el.parentElement;
        if (!scrollableEl) return;
        scrollableEl.scrollTo({
          top: 0,
          behavior: options?.behavior ?? "smooth",
        });
      },
      // Expondo o elemento de viewport para uso externo
      getViewportEl() {
        return viewportComp.value?.$el?.parentElement?.parentElement?.querySelector(
          "[data-reka-scroll-area-viewport]"
        )?.parentElement;
      },
      isNearBottom,
    });

    return () => (
      <ScrollAreaRoot
        type={props.type}
        class={cn("relative grow overflow-hidden focus-visible-default", props.rootClass)}
      >
        <ScrollAreaViewport
          ref={viewportComp}
          class={cn("h-full w-full rounded-[inherit] focus-visible-default", props.class)}
        >
          {slots.default?.()}
        </ScrollAreaViewport>
        {(props.orientation === "vertical" || props.orientation === "both") && (
          <ScrollBar orientation={"vertical"} scrollbarSize={props.scrollbarSize} />
        )}

        {(props.orientation === "horizontal" || props.orientation === "both") && (
          <ScrollBar orientation={"horizontal"} scrollbarSize={props.scrollbarSize} />
        )}
        <ScrollAreaCorner />
      </ScrollAreaRoot>
    );
  },
});

export function adaptAreaScrollDisplay(el: InstanceType<any>) {
  nextTick(() => {
    const div = el?.$el;
    if (!div) return;

    // take grandchild element
    const child = div?.children[0].children[0] as HTMLDivElement;
    if (!child) return;

    // change style display table to flex
    child.style.display = "flex";
    child.style.flexDirection = "column";
    child.style.height = "100%";
  });
}
